import { Injectable, OnDestroy } from '@angular/core';
import { CollabFileType } from '@common/collab/enums/collab-file-type.enum';
import { CollabService } from '@portal-core/collab/services/collab.service';
import { CurrentService } from '@portal-core/current/services/current.service';
import { groupBy } from 'lodash';
import { firstValueFrom } from 'rxjs';
import { v4 as uuidv4 } from 'uuid';

interface CollabPullFetchFile {
  branchName: string;
  filePath: string;
  projectId: number;
}

/**
 * A service that handles pulling or fetching collab docs from the remote branch.
 */
@Injectable({
  providedIn: 'root'
})
export class CollabPullFetchService implements OnDestroy {
  private pullFetchFiles: CollabPullFetchFile[] = [];
  private pullOrFetchTimeoutId: ReturnType<typeof setTimeout> = null;
  private enabled = false;

  constructor(private collabService: CollabService, private currentService: CurrentService) {
    // Polling starts automatically when the service is created
    this.start();
  }

  ngOnDestroy() {
    // Stop polling when the service is destroyed
    this.stop();
  }

  /**
   * Adds a file to the list of files to be pulled or fetched from the remote branch.
   * @param projectId The project id the file belongs to.
   * @param branchName The name of the branch to pull or fetch from.
   * @param filePath The path of the file to be pulled or fetched.
   */
  public addPullOrFetchFile(projectId: number, branchName: string, filePath: string) {
    const exists = this.pullFetchFiles.some(file => file.projectId === projectId && file.branchName === branchName && file.filePath === filePath);
    if (!exists) {
      this.pullFetchFiles.push({ projectId, branchName, filePath });
    }
  }

  /**
   * Clears the list of files to be pulled or fetched from the remote branch.
   */
  public clear() {
    this.pullFetchFiles = [];
  }

  /**
   * Removes a file from the list of files to be pulled or fetched from the remote branch.
   * @param projectId The project id the file belongs to.
   * @param branchName The name of the branch to pull or fetch from.
   * @param filePath The path of the file to no longer be pulled or fetched.
   */
  public removePullOrFetchFile(projectId: number, branchName: string, filePath: string) {
    this.pullFetchFiles = this.pullFetchFiles.filter(file => file.projectId !== projectId || file.branchName !== branchName || file.filePath !== filePath);
  }

  /**
   * Starts the polling for pulling or fetching collab docs from the remote branch.
   * Polling starts automatically when the service is created so this method only needs to be called if it is manually stopped.
   */
  public start() {
    if (!this.enabled) {
      this.enabled = true;
      this.schedulePullCollabDocFromRemote();
    }
  }

  /**
   * Stops the polling for pulling or fetching collab docs from the remote branch.
   * Polling stops automatically when the service is destroyed so this method only needs to be called if polling should be stopped before that.
   */
  public stop() {
    if (this.enabled) {
      this.enabled = false;
    }

    if (this.pullOrFetchTimeoutId) {
      clearTimeout(this.pullOrFetchTimeoutId);
      this.pullOrFetchTimeoutId = null;
    }
  }

  /**
   * If there is a current doc then it is pulled from the remote branch.
   * @returns A promise that resolves when the pull is completed. Resolves to null if there is no current doc.
   */
  private pullOrFetchCollabDocsFromRemote(): Promise<any> {
    // If there are no files to pull or fetch
    if (this.pullFetchFiles.length > 0) {
      // Group the files by projectId and branchName
      const groupedFiles = groupBy(this.pullFetchFiles, file => `${file.projectId}-${file.branchName}`);

      // Pull or fetch each group of files
      return Promise.allSettled(
        Object.values(groupedFiles).map(files => {
          const { projectId, branchName } = files[0];

          return firstValueFrom(this.collabService.pullOrFetchCollabDocs$({
            branchName,
            clientId: uuidv4(),
            filePaths: files.map(file => file.filePath),
            projectId,
            fileType: CollabFileType.Workspace,
            userId: this.currentService.getUserId()
          }));
        })
      );
    } else {
      return Promise.resolve();
    }
  }

  /**
   * Schedules a pull of the collab doc from the remote branch every 7 seconds.
   * The next pull is scheduled after the current pull is completed.
   */
  private schedulePullCollabDocFromRemote() {
    // If the polling is enabled then schedule the next request
    if (this.enabled) {
      this.pullOrFetchTimeoutId = setTimeout(() => {
        this.pullOrFetchTimeoutId = null;

        this.pullOrFetchCollabDocsFromRemote().finally(() => {
          this.schedulePullCollabDocFromRemote();
        });
      }, 7000);
    }
  }
}
